import CardSurface from '../card-surface/CardSurface'
import { useResidentTenancy } from '../../_hooks/use-resident-tenancy'
import {
  formatCents,
  formatTenancyStatus,
} from '../../_utilities/resident-format'
import { TenancySwitcherProps } from './types'
import {
  TenancyGrid,
  TenancyCardButton,
  UnitLabel,
  StatusPill,
  RentLine,
} from './styled'

/**
 * The renter's residence picker. Renders one selectable card per tenancy and
 * writes the choice back to the shared resident context, so every resident
 * surface (Home / Payments / Maintenance) stays on the same tenancy. Hidden
 * when the renter has a single tenancy unless `alwaysShow` is set — the Home
 * dashboard shows the residence even when there's only one, while Payments and
 * Maintenance only surface the picker when there's a real choice to make.
 *
 * @param {TenancySwitcherProps} props
 * @returns {JSX.Element | null}
 */
const TenancySwitcher = ({
  alwaysShow = false,
}: TenancySwitcherProps): JSX.Element | null => {
  const { tenancies, selectedTenancyId, selectTenancy } = useResidentTenancy()

  if (tenancies.length === 0) {
    return null
  }

  if (tenancies.length === 1 && alwaysShow === false) {
    return null
  }

  return (
    <TenancyGrid>
      {tenancies.map(tenancy => (
        <TenancyCardButton
          key={tenancy.id}
          type="button"
          isSelected={tenancy.id === selectedTenancyId}
          onClick={() => selectTenancy(tenancy.id)}
        >
          <CardSurface padding="tight">
            <UnitLabel>{tenancy.unitLabel}</UnitLabel>
            <StatusPill>{formatTenancyStatus(tenancy.status)}</StatusPill>
            <RentLine>{formatCents(tenancy.monthlyRentCents)} / month</RentLine>
          </CardSurface>
        </TenancyCardButton>
      ))}
    </TenancyGrid>
  )
}

export default TenancySwitcher
